import { IProjectData } from "../../models/shared/IProjectData";
import { SuggestedProject, Suggestion } from "./SuggestedProject";
import { OfficeWrapper } from "../OfficeWrapper";
import { OfficeRoamingSettings } from "../OfficeRoamingSettings";
import { ProjectCollection } from "./ProjectCollection";
import FiledProject from "../../models/FiledProject";
import { ParsedPair } from "../../models/ProjectItemDetails";
import { Logger } from "../Logger";

const sortBy = require("array-sort-by");

export type SmartFilingProjectInput = IProjectData | { key: string; name: string };

export class SmartFilingManagerFluent9 {
    private readonly MIN_VALID_CHAR_LENGTH = 2;

    private fullProjectListReference: { [key: string]: IProjectData } = {};
    private filedProjectCollection: ProjectCollection<FiledProject>;
    private filedProjectHistoryLoaded = false;
    private cachedBody: string | null = null;
    private cachedSubject: string | null = null;
    private cachedConversationId: string | null = null;

    constructor(
        private officeWrapper: OfficeWrapper,
        private officeRoamingSettings: OfficeRoamingSettings,
        private logger: Logger
    ) {
        this.filedProjectCollection = new ProjectCollection<FiledProject>();
    }

    public async getSuggestedProjects(
        projects: SmartFilingProjectInput[],
        parseEmailSubject?: boolean,
        useSimpleInput?: boolean
    ): Promise<SuggestedProject[]> {
        if (!this.officeWrapper.currentContextItem) return [];

        // Invalidate cache if the conversation has changed (e.g. navigating between emails)
        const currentConversationId = this.officeWrapper.currentContextItem.conversationId ?? null;
        if (currentConversationId !== this.cachedConversationId) {
            this.cachedBody = null;
            this.cachedSubject = null;
            this.cachedConversationId = currentConversationId;
        }

        // When useSimpleInput is true, normalize { key, name } to IProjectData
        const normalizedProjects: IProjectData[] = useSimpleInput
            ? projects.map((p) => {
                  const simple = p as { key: string; name: string };
                  return { nrn: simple.key, name: simple.name, displayName: simple.name };
              })
            : (projects as IProjectData[]);

        normalizedProjects.forEach((proj) => {
            this.fullProjectListReference[proj.nrn] = proj;
        });

        await this.loadFiledProjectHistory();

        let body: string;
        let subject: string;
        if (this.cachedBody !== null && this.cachedSubject !== null) {
            body = this.cachedBody;
            subject = this.cachedSubject;
        } else {
            [body, subject] = await Promise.all([
                this.officeWrapper.getCurrentEmailBody(),
                this.officeWrapper.getCurrentEmailSubject(),
            ]);
            this.cachedBody = body;
            this.cachedSubject = subject;
        }
        const { allPairs, lastPair, lastProjectName } = this.officeWrapper.parseSubjectGetLast(subject);
        const suggestedProjects = this.setProjectItemsWeight(
            normalizedProjects,
            body.toLocaleLowerCase(),
            subject.toLocaleLowerCase(),
            parseEmailSubject,
            lastPair
        );

        suggestedProjects.forEach((suggestedProject) => {
            const originalProject: any = normalizedProjects.find((proj) => proj.nrn === suggestedProject.nrn);
            if (originalProject) {
                suggestedProject.nixEnabled = originalProject.nixEnabled;
                suggestedProject.isDisable = originalProject?.isDisable;
                suggestedProject.code = originalProject?.code;
                suggestedProject.number = originalProject?.number;
            }
        });

        this.logSuggestedProjectRankings(suggestedProjects);

        if (suggestedProjects.length) {
            sortBy(suggestedProjects, (item: SuggestedProject) => [
                -item.suggestionWeight,
                -(item.dateFiled || Number.MAX_SAFE_INTEGER),
            ]);
        }

        this.logger.info(`${suggestedProjects.length} project suggestions`);
        return suggestedProjects;
    }

    public async addToFiledHistory(
        project: { key: string; name: string },
        conversationId: string,
        sender: string
    ): Promise<void> {
        // Ensure existing history is loaded before modifying, in case getSuggestedProjects hasn't been called yet
        if (!this.filedProjectHistoryLoaded) {
            const existingFiledProjects = (this.officeRoamingSettings.getFiledProjects() || []).map((p) => {
                p.t = p.t || [];
                return p;
            });
            this.filedProjectCollection = new ProjectCollection<FiledProject>(existingFiledProjects);
            this.filedProjectHistoryLoaded = true;
        }

        let filedProject: FiledProject | undefined;
        if (this.filedProjectCollection.has(project.key)) {
            filedProject = this.filedProjectCollection.get(project.key);
            const uniquifiedThreads = new Set(filedProject.t);
            uniquifiedThreads.add(conversationId);
            filedProject.t = Array.from(uniquifiedThreads);
            filedProject.d = new Date();
        } else {
            filedProject = new FiledProject(project.key, conversationId, sender);
        }
        filedProject.c++;
        this.filedProjectCollection.add(filedProject);

        await this.officeRoamingSettings.saveWithRetry(
            {
                filedProjects: this.filedProjectCollection.toArray(),
            },
            true
        );
    }

    private async loadFiledProjectHistory(): Promise<void> {
        let filedProjects = this.officeRoamingSettings.getFiledProjects() || [];
        filedProjects = filedProjects.filter((storedProject) => !!this.fullProjectListReference[storedProject.nrn]);

        this.filedProjectCollection = new ProjectCollection<FiledProject>(filedProjects);
        this.filedProjectHistoryLoaded = true;
    }

    private setProjectItemsWeight(
        projects: IProjectData[],
        body: string,
        subject: string,
        parseEmailSubject?: boolean,
        lastPair?: ParsedPair
    ): SuggestedProject[] {
        const suggestedProjects = new ProjectCollection<SuggestedProject>();
        for (const project of projects) {
            const suggestion = new Suggestion();
            const lowerCaseProjectName = project.displayName.toLocaleLowerCase();
            const projectCode = (project as any).code?.toLocaleLowerCase();
            let projectMatch = false;
            const projectNameParts = lowerCaseProjectName.split(" - ");
            for (const namePart of projectNameParts) {
                if (namePart && namePart.length >= this.MIN_VALID_CHAR_LENGTH) {
                    const trimmedNamedPart = namePart.trim();
                    if (body && (body.includes(trimmedNamedPart) || trimmedNamedPart.includes(body))) {
                        projectMatch = !!suggestion.bodyKeywordMatch();
                    }
                    if (parseEmailSubject) {
                        if (
                            subject &&
                            (lastPair?.combined.includes(projectCode) || trimmedNamedPart.includes(projectCode))
                        ) {
                            projectMatch = !!suggestion.subjectKeywordMatch();
                        }
                    } else {
                        if (subject && (subject.includes(projectCode) || trimmedNamedPart.includes(projectCode))) {
                            projectMatch = !!suggestion.subjectKeywordMatch();
                        }
                    }
                }
            }
            if (projectMatch) {
                let suggestedProject = suggestedProjects.get(project.nrn);
                if (!suggestedProject) {
                    suggestedProject = new SuggestedProject(project, suggestion);
                    suggestedProjects.add(suggestedProject);
                }
            }
            // project is a match when project found in subject of email and instance called from project item component
            const isProjectNameMatch = lastPair && parseEmailSubject;
            this.adjustFiledProjectSuggestionWeight(project, suggestedProjects, isProjectNameMatch, lastPair);
        }
        return suggestedProjects.toArray();
    }

    private adjustFiledProjectSuggestionWeight(
        project: IProjectData,
        suggestedProjects: ProjectCollection<SuggestedProject>,
        projectMatch?: boolean,
        lastPair?: ParsedPair
    ): void {
        const filedProject = this.filedProjectCollection.get(project.nrn);
        let suggestion = new Suggestion();
        if (!filedProject) {
            // if not a filed project, still need project in subject to weight higher when called from project item
            if (projectMatch) {
                if (project.code === lastPair?.projectCode) {
                    const newSuggestedProject = suggestedProjects.get(project?.nrn);
                    suggestion.isProjectNameMatch();
                    newSuggestedProject.suggestion = suggestion;
                    return;
                }
                return;
            } else {
                return;
            }
        }
        let suggestedProject = suggestedProjects.get(filedProject?.nrn);
        if (suggestedProject) {
            suggestion = suggestedProject.suggestion;
            suggestedProject.filingFrequency = filedProject.c;
            suggestedProject.dateFiled = filedProject.d;
        } else {
            suggestedProject = new SuggestedProject(filedProject, suggestion);

            if (suggestedProjects.has(project.nrn)) {
                suggestion = suggestedProjects.get(project.nrn).suggestion;
            }
            suggestedProject = new SuggestedProject(filedProject, suggestion);
            suggestedProject.name = project.name;
        }
        if (filedProject.t?.includes(this.officeWrapper.currentConversationId)) {
            suggestion.isThread();
        }

        const senderAddress = this.officeWrapper.getSenderEmailAddress();
        const userAddress = this.officeWrapper.userProfileEmailAddress;
        if (senderAddress && senderAddress !== userAddress) {
            const userDomain = this.officeWrapper.userProfileEmailDomain;
            const senderDomain = this.officeWrapper.getContextMailboxItemSenderEmailDomain();

            const senderMatch = filedProject?.s === senderAddress;
            if (senderMatch) {
                if (userDomain === senderDomain) {
                    suggestion.isInternalSender();
                } else {
                    suggestion.isExternalSender();
                }
            }
        }

        suggestion.isRecent(); // if it's in the FiledProjects it's recent
        // add higher weighting for if project is parsed from email subject in project item
        if (projectMatch && project.code === lastPair?.projectCode) {
            suggestion.isProjectNameMatch();
        }
        suggestedProject.suggestion = suggestion;
        suggestedProjects.add(suggestedProject);
    }

    private logSuggestedProjectRankings(projectsArray: SuggestedProject[]): void {
        const logFormattedSuggestionsList = projectsArray.map((project) => {
            const projectData: { [key: string]: { [key: string]: any } } = {};
            projectData[project.name] = {
                "Suggestion Types Hit": project.rankedTypes,
                "Total Suggestion Weight": project.suggestionWeight,
                "Last Filed Date": project.dateFiled?.toISOString(),
            };
            return projectData;
        });
        this.logger.info(`Suggestions List: ${JSON.stringify(logFormattedSuggestionsList)}`);
    }
}
